import { AUDIT_DAYS, AUDIT_TAB_CATEGORIES, auditEventInfo, auditTabs, startOfZonedDay, type AdminMembersDto, type AuditCategory, type AuditEntryDto, type AuditIcon, type AuditPageDto, type AuditTab } from '@dbrain/shared';
import { Chip, Icons, Menu, MenuItem, type IconComponent } from '@dbrain/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import { useCompanyZone, useMe } from '../../api/hooks.js';
import { CsvLink, DataTable, FilterMenu, Pagination, SearchBox, useListParams, type Column } from '../../components/admin/DataTable.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { eventFilterOf, MICROSOFT_AUDIT_PREFIX } from '../../components/microsoft365/admin/model.js';
import { useCapabilities } from '../../experience.js';
import { useAdminDomains } from './AccessPage.js';
import { AdminLayout, PillTabs } from './AdminLayout.js';
import { auditView, dayKey } from '@dbrain/app-core/audit-details';

/**
 * Yönetim → Denetim kaydı (YonetimDenetim.dc.html, KurumsalDenetim.dc.html, 06 §23.7): admin, security and
 * system events in one log, newest first, grouped by day, in words, under tabs by kind with their counts
 * (D-140). Search covers people, events and details; a person is the actor or the target. Conversation
 * content never appears here.
 */
export const AUDIT_ICONS: Record<AuditIcon, IconComponent> = {
  login: Icons.login,
  logout: Icons.logout,
  alert: Icons.error,
  people: Icons.members,
  budget: Icons.gauge,
  workspace: Icons.workspace,
  skill: Icons.skills,
  link: Icons.plug,
  shield: Icons.shield,
  server: Icons.platform,
  download: Icons.download,
  key: Icons.key,
  model: Icons.models,
  backup: Icons.database,
  update: Icons.regenerate,
  lock: Icons.lock,
  // Phase 16c (B2cPlatformDenetim)
  mail: Icons.mail,
  pro: Icons.skills,
  card: Icons.card,
};

/** The tab a category belongs to; skills and connections have their own names in the Tür column (D-140). */
const KIND_OF: Record<AuditCategory, string> = {
  ...(Object.fromEntries(Object.entries(AUDIT_TAB_CATEGORIES).flatMap(([tab, categories]) => categories.map((c) => [c, tab]))) as Record<AuditCategory, string>),
  skills: 'skills',
  integrations: 'integrations',
};

/** "Modeller", "Giriş ve erişim", "Sistem": an event's kind, as the tabs name it. */
export function auditKindLabel(action: string, t: (key: string) => string): string {
  return t(`admin.audit.kinds.${KIND_OF[auditEventInfo(action).category]}`);
}

/** "Kişi: Herkes ⌄": a small search over the members, ten at a time. */
function PersonFilter({ person, who, onChange }: { person: string | undefined; who: string | undefined; onChange: (person: { id: string; name: string } | null) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const found = useQuery({
    queryKey: ['admin', 'members', 'pick', q],
    queryFn: () => api<AdminMembersDto>('GET', `/api/admin/members?size=10&sort=name${q ? `&q=${encodeURIComponent(q)}` : ''}`),
    placeholderData: keepPreviousData,
  });
  return (
    <Menu
      trigger={
        <>
          <span>
            {t('admin.audit.person')}: {person ? (who ?? t('admin.audit.chosen')) : t('admin.audit.everyone')}
          </span>
          <Icons.chevronDown size={14} />
        </>
      }
      triggerClassName="dt-filter"
      panelClassName="dt-filter__panel"
      data-testid="audit-person"
    >
      <input className="ui-input ui-input--sm person-filter__search" type="search" value={q} placeholder={t('admin.audit.personSearch')} aria-label={t('admin.audit.personSearch')} onChange={(e) => setQ(e.target.value)} data-testid="audit-person-search" />
      <MenuItem aria-pressed={!person} onClick={() => onChange(null)}>
        {t('admin.audit.everyone')}
      </MenuItem>
      {(found.data?.rows ?? []).map((m) => (
        <MenuItem key={m.userId} aria-pressed={m.userId === person} hint={m.email} onClick={() => onChange({ id: m.userId, name: m.name })} data-testid="audit-person-option">
          {m.name}
        </MenuItem>
      ))}
    </Menu>
  );
}

/**
 * "Olay: Microsoft 365" (MsDenetim, Phase 15e): every `microsoft.*` event as an action prefix, so the tabs count
 * the same rows. Another chosen event (a deep link such as a skill's failed loads) shows by its title.
 */
function EventFilter({ action, onChange }: { action: string | undefined; onChange: (action: string | null) => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('en') ? 'en' : 'tr';
  const value = eventFilterOf(action);
  const options = [{ value: 'microsoft', label: t('microsoft365.admin.audit.filter.microsoft') }];
  if (value === 'chosen' && action) options.push({ value: 'chosen', label: action.split(',').map((a) => auditEventInfo(a).title[lang]).join(', ') });
  return (
    <FilterMenu
      label={t('microsoft365.admin.audit.filter.label')}
      value={value ?? undefined}
      allLabel={t('microsoft365.admin.audit.filter.all')}
      options={options}
      onChange={(next) => {
        if (next === 'microsoft') onChange(MICROSOFT_AUDIT_PREFIX);
        else if (next === null) onChange(null);
      }}
      testId="audit-event"
    />
  );
}

export function AuditPage() {
  const { t, i18n } = useTranslation();
  const capabilities = useCapabilities();
  // Microsoft 365 is a firm's (a personal space has none of its events)
  const firm = useMe().data?.tenant.kind === 'organization';
  const list = useListParams<'category' | 'tab' | 'person' | 'who' | 'warnings' | 'action'>({ days: '7' });
  const query = new URLSearchParams([...new URLSearchParams(list.query())].filter(([key]) => key !== 'who')).toString();
  const entries = useQuery({ queryKey: ['admin', 'audit', query], queryFn: () => api<AuditPageDto>('GET', `/api/admin/audit?${query}`), placeholderData: keepPreviousData });
  const domains = useAdminDomains();
  const names = new Map((domains.data?.rows ?? []).map((d) => [d.code, d.name]));
  const data = entries.data;
  const days = Number(list.get('days') ?? 7);
  // the license is a kurumsal tab (13f); bireysel has no license events
  const tabs = auditTabs.filter((tab) => tab !== 'license' || capabilities.licensing);
  const tab = (tabs as readonly string[]).includes(list.get('tab') ?? '') ? (list.get('tab') as AuditTab) : 'all';
  const warnings = list.get('warnings') === '1';
  const microsoft = list.get('action') === MICROSOFT_AUDIT_PREFIX;
  // the company's days (D-119)
  const zone = useCompanyZone();
  const today = dayKey(new Date().toISOString(), zone);
  const yesterday = dayKey(new Date(startOfZonedDay(new Date(), zone).getTime() - 1).toISOString(), zone);
  const groupLabel = (key: string) => {
    const [y, mo, d] = key.split('-').map(Number);
    const date = new Date(y as number, (mo as number) - 1, d).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
    if (key === today) return t('admin.audit.today', { date });
    if (key === yesterday) return t('admin.audit.yesterday', { date });
    return date;
  };
  const columns: Column<AuditEntryDto>[] = [
    { id: 'time', header: t('admin.audit.columns.time'), width: 'xs', cell: (e) => <time dateTime={e.createdAt}>{new Date(e.createdAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', timeZone: zone })}</time> },
    {
      id: 'event',
      header: t('admin.audit.columns.event'),
      width: 'xl',
      cell: (e) => {
        const view = auditView(e, t, i18n.language, (code) => names.get(code) ?? code);
        const Icon = AUDIT_ICONS[view.icon];
        return (
          <span className={`audit-event${view.failure ? ' audit-event--failure' : ''}`}>
            <span className={`audit-event__icon audit-event__icon--${view.icon}`} aria-hidden="true">
              <Icon size={14} />
            </span>
            <span className="audit-event__title">{view.title}</span>
          </span>
        );
      },
    },
    { id: 'kind', header: t('admin.audit.columns.kind'), width: 'md', cell: (e) => <span className="audit-kind">{auditKindLabel(e.action, t)}</span> },
    { id: 'who', header: t('admin.audit.columns.who'), width: 'lg', cell: (e) => auditView(e, t, i18n.language).who ?? <span className="muted">{t('audit.system')}</span> },
    {
      id: 'details',
      header: t('admin.audit.columns.details'),
      cell: (e) => {
        const view = auditView(e, t, i18n.language, (code) => names.get(code) ?? code);
        return (
          <span className="audit-detail">
            {view.detail}
            {view.ip ? <span className="audit-detail__ip">{view.detail ? ' · ' : ''}{view.ip}</span> : null}
          </span>
        );
      },
    },
  ];
  return (
    <AdminLayout section="audit" title={t('admin.sections.audit')}>
      <div className="stack admin-audit">
        {microsoft ? (
          <p className="muted" data-testid="audit-microsoft-note">
            {t('microsoft365.admin.audit.note')}
          </p>
        ) : (
          <p className="muted">{t('admin.audit.hint')}</p>
        )}
        <PillTabs
          label={t('admin.audit.tabsLabel')}
          value={tab}
          // "Olay: Microsoft 365" is a filter of the toolbar and stays; a deep link's exact events do not
          onChange={(next) => list.set({ tab: next === 'all' ? null : next, category: null, action: microsoft ? MICROSOFT_AUDIT_PREFIX : null })}
          items={tabs.map((id) => ({ id, label: t(`admin.audit.tabs.${id}`), count: data?.tabs[id], testId: `audit-tab-${id}` }))}
        />
        <div className="dt-toolbar">
          <SearchBox value={list.get('q') ?? ''} onChange={(q) => list.set({ q })} placeholder={t('admin.audit.search')} testId="audit-search" />
          {firm ? <EventFilter action={list.get('action')} onChange={(action) => list.set({ action })} /> : null}
          <PersonFilter person={list.get('person')} who={list.get('who')} onChange={(p) => list.set({ person: p?.id ?? null, who: p?.name ?? null })} />
          <FilterMenu
            label={t('admin.audit.date')}
            value={String(days)}
            allLabel={t('admin.audit.dates.d7')}
            options={AUDIT_DAYS.map((d) => ({ value: String(d), label: t(`admin.audit.dates.d${d}`) }))}
            onChange={(value) => list.set({ days: value ?? '7' })}
            testId="audit-days"
          />
          <Chip active={warnings} onClick={() => list.set({ warnings: warnings ? null : '1' })} data-testid="audit-warnings">
            <span className="ui-dot ui-dot--danger" aria-hidden="true" />
            {t('admin.audit.warnings')}
            {data ? <span className="dt-chips__count">{data.warnings.toLocaleString(i18n.language)}</span> : null}
          </Chip>
          <span className="dt-toolbar__end">
            <CsvLink href={`/api/admin/audit.csv?${query}`} label={t('admin.audit.csv')} testId="audit-export" />
          </span>
        </div>
        <ErrorAlert error={entries.error} />
        {data ? (
          <DataTable
            label={t('admin.sections.audit')}
            columns={columns}
            rows={data.rows}
            rowKey={(e) => e.id}
            groupOf={(e) => dayKey(e.createdAt, zone)}
            groupLabel={groupLabel}
            rowClassName={(e) => (auditView(e, t, i18n.language).failure ? 'dt__row--failure' : undefined)}
            rowTestId={(e) => `audit-${e.action}`}
            loading={entries.isFetching}
            empty={t('audit.empty')}
            data-testid="audit-table"
            footer={
              <Pagination
                page={list.page}
                size={list.size}
                total={data.total}
                noun={t('admin.audit.noun')}
                note={t('admin.audit.note')}
                onPage={(page) => list.set({ page: String(page) })}
                onSize={(size) => list.set({ size: String(size) })}
              />
            }
          />
        ) : null}
      </div>
    </AdminLayout>
  );
}
